import React from "react";
import Slider from "./Slider";
import Toggle from "./Toggle";
import PresetButton from "./PresetButton";
import Icon from "../../../components/AppIcon";
import { PRESETS } from "../constants/presets";

// 1. Echo & Delay Module
export const EchoDelayModule = ({
  params,
  onUpdateParam,
  disabled = false,
}) => {
  return (
    <div className="bg-card border border-border rounded-lg p-6 terminal-glow">
      <div className="flex items-center space-x-2 border-b border-border pb-3 mb-4">
        <Icon name="CornerRightDown" size={16} className="text-primary" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Echo & Delay Module
        </h3>
      </div>
      <div className="space-y-5">
        <Slider
          label="Delay Time"
          min={0}
          max={1000}
          step={10}
          value={params.delayTime}
          defaultValue={250}
          unit=" ms"
          disabled={disabled}
          onChange={(val) => onUpdateParam("delayTime", val)}
        />

        <div className="grid grid-cols-2 gap-4">
          <Slider
            label="Feedback"
            min={0}
            max={0.95}
            step={0.01}
            value={params.feedback}
            defaultValue={0.45}
            disabled={disabled}
            formatValue={(v) => `${Math.round(v * 100)}%`}
            onChange={(val) => onUpdateParam("feedback", val)}
          />
          <Slider
            label="Echo Repeats"
            min={1}
            max={20}
            step={1}
            value={params.repeats}
            defaultValue={5}
            disabled={disabled}
            formatValue={(v) => `${v}`}
            onChange={(val) => onUpdateParam("repeats", val)}
          />
        </div>

        <Slider
          label="Echo Volume (Wet Gain)"
          min={0}
          max={1}
          step={0.05}
          value={params.echoVolume}
          defaultValue={0.5}
          disabled={disabled}
          formatValue={(v) => `${Math.round(v * 100)}%`}
          onChange={(val) => onUpdateParam("echoVolume", val)}
        />

        <Slider
          label="Dry / Wet Balance"
          min={0}
          max={100}
          step={5}
          value={params.dryWetMix}
          defaultValue={50}
          disabled={disabled}
          formatValue={(v) => {
            if (v === 0) return "100% DRY";
            if (v === 100) return "100% WET";
            return `${100 - v}% D / ${v}% W`;
          }}
          onChange={(val) => onUpdateParam("dryWetMix", val)}
        />
      </div>
    </div>
  );
};

// 2. Environment & Pan Module
export const EnvironmentPanModule = ({
  params,
  onUpdateParam,
  disabled = false,
}) => {
  return (
    <div className="bg-card border border-border rounded-lg p-6 terminal-glow flex flex-col justify-between">
      <div>
        <div className="flex items-center space-x-2 border-b border-border pb-3 mb-4">
          <Icon name="Compass" size={16} className="text-primary" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            Environment & Pan
          </h3>
        </div>
        
        <div className="space-y-5">
          <Toggle
            label="Reverb Space"
            checked={params.reverb}
            disabled={disabled}
            onChange={(val) => onUpdateParam("reverb", val)}
          />

          <Slider
            label="Reverb Mix"
            min={0}
            max={1}
            step={0.05}
            value={params.reverbVolume}
            defaultValue={0.3}
            disabled={disabled || !params.reverb}
            formatValue={(v) => `${Math.round(v * 100)}%`}
            onChange={(val) => onUpdateParam("reverbVolume", val)}
          />

          <Slider
            label="Stereo Pan"
            min={-1}
            max={1}
            step={0.1}
            value={params.stereoPan}
            defaultValue={0}
            disabled={disabled}
            formatValue={(v) => {
              if (v === 0) return "C";
              return v < 0 ? `${Math.round(Math.abs(v) * 100)}% L` : `${Math.round(v * 100)}% R`;
            }}
            onChange={(val) => onUpdateParam("stereoPan", val)}
          />
        </div>
      </div>
      
      <div className="border-t border-border/50 pt-4 mt-4">
        <Slider
          label="Master Gain Output"
          min={0}
          max={1}
          step={0.05}
          value={params.masterVolume}
          defaultValue={0.8}
          disabled={disabled}
          formatValue={(v) => `${Math.round(v * 100)}%`}
          onChange={(val) => onUpdateParam("masterVolume", val)}
        />
      </div>
    </div>
  );
};

// 3. Acoustic Filters & Equalizer Module
export const AcousticFiltersEQModule = ({
  params,
  onUpdateParam,
  disabled = false,
}) => {
  return (
    <div className="bg-card border border-border rounded-lg p-6 terminal-glow">
      <div className="flex items-center space-x-2 border-b border-border pb-3 mb-4">
        <Icon name="Cpu" size={16} className="text-primary" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Acoustic Filters & Equalizer
        </h3>
      </div>

      <div className="flex flex-col gap-6">
        {/* Toggle Switches: Dip switches style column */}
        <div className="flex flex-col gap-3.5">
          <Toggle
            label="Noise Gate Reduction"
            checked={params.noiseReduction}
            disabled={disabled}
            onChange={(val) => onUpdateParam("noiseReduction", val)}
          />

          <Toggle
            label="Vocal Dynamics Compressor"
            checked={params.compressor}
            disabled={disabled}
            onChange={(val) => onUpdateParam("compressor", val)}
          />
        </div>

        {/* 3-Band Parametric EQ: Stacked Column View */}
        <div className="space-y-4 pt-4 border-t border-border/40">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
            3-Band Parametric EQ
          </span>
          <div className="flex flex-col gap-4">
            <Slider
              label="BASS [250Hz]"
              min={-12}
              max={12}
              step={1}
              value={params.eqLow}
              defaultValue={0}
              disabled={disabled}
              unit=" dB"
              formatValue={(v) => (v > 0 ? `+${v} dB` : `${v} dB`)}
              onChange={(val) => onUpdateParam("eqLow", val)}
            />

            <Slider
              label="MID [1.5kHz]"
              min={-12}
              max={12}
              step={1}
              value={params.eqMid}
              defaultValue={0}
              disabled={disabled}
              unit=" dB"
              formatValue={(v) => (v > 0 ? `+${v} dB` : `${v} dB`)}
              onChange={(val) => onUpdateParam("eqMid", val)}
            />

            <Slider
              label="TREBLE [4kHz]"
              min={-12}
              max={12}
              step={1}
              value={params.eqHigh}
              defaultValue={0}
              disabled={disabled}
              unit=" dB"
              formatValue={(v) => (v > 0 ? `+${v} dB` : `${v} dB`)}
              onChange={(val) => onUpdateParam("eqHigh", val)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. Acoustic Preset Console Module
export const AcousticPresetConsole = ({
  params,
  onLoadPreset,
  disabled = false,
}) => {
  return (
    <div className="bg-card border border-border rounded-lg p-6 terminal-glow">
      <div className="flex items-center space-x-2 border-b border-border pb-3 mb-4">
        <Icon name="Sliders" size={16} className="text-primary" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Acoustic Preset Console
        </h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
        {Object.values(PRESETS).map((preset) => {
          const isActive =
            params.delayTime === preset.delayTime &&
            Math.abs(params.feedback - preset.feedback) < 0.02 &&
            params.dryWetMix === preset.dryWetMix &&
            params.reverb === preset.reverb &&
            params.compressor === preset.compressor &&
            params.noiseReduction === preset.noiseReduction;

          return (
            <PresetButton
              key={preset.id}
              name={preset.name}
              description={preset.description}
              active={isActive}
              disabled={disabled}
              onClick={() => onLoadPreset(preset)}
            />
          );
        })}
      </div>
    </div>
  );
};
